import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { ListSkeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { freightService } from '../services';
import type { FreightLocationDto } from '../types';

interface Props {
  value: FreightLocationDto | null;
  onSelect: (loc: FreightLocationDto | null) => void;
  disabled?: boolean;
  id?: string;
}

// All locations are returned in one call from the cache (~400 rows), so we filter in
// memory rather than round-tripping per keystroke. The first open primes the list;
// subsequent opens reuse the cached array.
export function FreightLocationCombobox({ value, onSelect, disabled, id }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [allLocations, setAllLocations] = useState<FreightLocationDto[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const ensureLoaded = useCallback(async () => {
    if (allLocations || isLoading) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const list = await freightService.getLocations();
      setAllLocations(list);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load locations');
    } finally {
      setIsLoading(false);
    }
  }, [allLocations, isLoading]);

  const filtered = useMemo(() => {
    if (!allLocations) return [];
    const q = query.trim().toLowerCase();
    if (!q) return allLocations.slice(0, 50);
    return allLocations
      .filter((l) =>
        l.name.toLowerCase().includes(q) || l.cityName.toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [allLocations, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, filtered.length]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        void ensureLoaded();
        // Focus the input on the next tick so Radix's autoFocus inside the
        // popover doesn't steal it back.
        requestAnimationFrame(() => inputRef.current?.focus());
      } else {
        setQuery('');
      }
    },
    [ensureLoaded],
  );

  const pick = useCallback(
    (loc: FreightLocationDto) => {
      onSelect(loc);
      setOpen(false);
      setQuery('');
    },
    [onSelect],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const choice = filtered[activeIndex];
      if (choice) pick(choice);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const label = value ? `${value.cityName} — ${value.name}` : 'Select destination…';

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div className="flex w-full items-center gap-1">
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              'h-9 flex-1 justify-between gap-2 px-3 text-left font-normal',
              !value && 'text-muted-foreground',
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <Search className="h-3.5 w-3.5 shrink-0 opacity-60" />
              {/* Clipped text recoverable on hover (UI gate 6) — native title on this one bounded element. */}
              <span className="truncate" title={label}>{label}</span>
            </span>
          </Button>
        </PopoverTrigger>
        {value && !disabled && (
          // Icon-only (UI gate 5): shared Tooltip plus title and aria-label in the same words.
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => onSelect(null)}
                  aria-label="Clear destination"
                  title="Clear destination"
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Clear destination</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <PopoverContent
        className="w-[420px] max-w-[calc(100vw-2rem)] p-0"
        align="start"
        sideOffset={4}
      >
        <div className="border-b p-2">
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Filter by city or location…"
            className="h-8"
            autoComplete="off"
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {isLoading && <ListSkeleton rows={4} className="p-2" />}
          {/* Recoverable failure: after a failed load allLocations is null and isLoading false, so
              ensureLoaded fetches again. */}
          {loadError && !isLoading && (
            <div className="flex items-center justify-between gap-2 p-4 text-sm">
              <span className="text-destructive">{loadError}</span>
              <Button type="button" variant="outline" size="sm" onClick={() => void ensureLoaded()}>
                Retry
              </Button>
            </div>
          )}
          {!isLoading && !loadError && filtered.length === 0 && (
            <EmptyState size="compact" title="No matching locations" />
          )}
          {filtered.map((loc, i) => {
            const selected = value?.id === loc.id;
            const active = i === activeIndex;
            return (
              <button
                key={loc.id}
                type="button"
                onClick={() => pick(loc)}
                onMouseEnter={() => setActiveIndex(i)}
                className={cn(
                  'flex w-full flex-col items-start gap-0 rounded px-2 py-1.5 text-left text-sm',
                  active && 'bg-accent text-accent-foreground',
                  selected && 'font-medium',
                )}
              >
                {/* Native titles per row, never a Radix Tooltip per row (UI-GUIDELINES §8). */}
                <span className="max-w-full truncate" title={loc.cityName}>{loc.cityName}</span>
                <span className="max-w-full truncate text-xs text-muted-foreground" title={loc.name}>{loc.name}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
